import {
  GetAllClientsParams,
  GetAllTouchpointsForCampaignParams,
  GetFoldersParams,
  GetAllProgramTemplatesParams,
} from "generated/schemas";

import { PaginatedRequestOptions } from "types/pagination";
import { FiltersToApplyOnCampaignsPage } from "containers/admin/clients/client/campaigns-page/components/filters-on-campaigns-table/filters-on-campaigns-table.types";
import { InboxFilter } from "types/inbox";

/** Pass meta: { skipInvalidation: true } in bulk operations; then invalidate once after the batch */
export interface BulkMutationMeta {
  skipInvalidation: boolean;
}

export const DEFAULT_TOUCHPOINTS_LIST_SORT: NonNullable<
  GetAllTouchpointsForCampaignParams["sort"]
> = ["lastModifiedDate,desc"];

export function normalizeTouchpointListParams(
  params?: GetAllTouchpointsForCampaignParams,
): GetAllTouchpointsForCampaignParams {
  if (!params) {
    return { unpaged: true, sort: DEFAULT_TOUCHPOINTS_LIST_SORT };
  }
  const { sort, page, size, ...rest } = params;
  return {
    ...rest,
    unpaged: true,
    sort: sort && sort.length > 0 ? sort : DEFAULT_TOUCHPOINTS_LIST_SORT,
  };
}

export const touchpointKeys = {
  all: ["touchpoints"] as const,
  lists: () => [...touchpointKeys.all, "list"] as const,
  list: (clientId: string, campaignId: string, params?: GetAllTouchpointsForCampaignParams) =>
    [
      ...touchpointKeys.lists(),
      { clientId, campaignId, ...normalizeTouchpointListParams(params) },
    ] as const,
  jobStates: (clientId: string, campaignId: string) =>
    [...touchpointKeys.all, "job-states", { clientId, campaignId }] as const,
  details: () => [...touchpointKeys.all, "detail"] as const,
  detail: (clientId: string, campaignId: string, id: string) =>
    [...touchpointKeys.details(), { clientId, campaignId, id }] as const,
  latestBatches: () => [...touchpointKeys.all, "latest-batch"] as const,
  latestBatch: (clientId: string, campaignId: string, touchpointId: string) =>
    [...touchpointKeys.latestBatches(), { clientId, campaignId, touchpointId }] as const,
};

export const touchpointVersionKeys = {
  all: ["touchpoint-versions"] as const,
  lists: () => [...touchpointVersionKeys.all, "list"] as const,
  listScope: (clientId: string, campaignId: string, touchpointId: string) =>
    [...touchpointVersionKeys.lists(), { clientId, campaignId, touchpointId }] as const,
  list: (
    clientId: string,
    campaignId: string,
    touchpointId: string,
    params?: PaginatedRequestOptions,
  ) => [...touchpointVersionKeys.listScope(clientId, campaignId, touchpointId), { ...params }] as const,
  details: () => [...touchpointVersionKeys.all, "detail"] as const,
  detail: (clientId: string, campaignId: string, touchpointId: string, id: string) =>
    [...touchpointVersionKeys.details(), { clientId, campaignId, touchpointId, id }] as const,
};

export const commentKeys = {
  all: ["touchpoint-comments"] as const,
  scope: (clientId: string, campaignId: string, touchpointId: string) =>
    [...commentKeys.all, { clientId, campaignId, touchpointId }] as const,
  lists: (clientId: string, campaignId: string, touchpointId: string) =>
    [...commentKeys.scope(clientId, campaignId, touchpointId), "list"] as const,
  list: (
    clientId: string,
    campaignId: string,
    touchpointId: string,
    params?: Record<string, unknown>,
  ) => [...commentKeys.lists(clientId, campaignId, touchpointId), { ...params }] as const,
  summary: (clientId: string, campaignId: string, touchpointId: string) =>
    [...commentKeys.scope(clientId, campaignId, touchpointId), "summary"] as const,
  replies: (clientId: string, campaignId: string, touchpointId: string, commentId: string) =>
    [
      ...commentKeys.scope(clientId, campaignId, touchpointId),
      "replies",
      commentId,
    ] as const,
};

export const folderKeys = {
  all: ["folders"] as const,
  lists: () => [...folderKeys.all, "list"] as const,
  list: (clientId: string, campaignId: string, params?: GetFoldersParams) =>
    [...folderKeys.lists(), { clientId, campaignId, ...params }] as const,
  details: () => [...folderKeys.all, "detail"] as const,
  detail: (clientId: string, campaignId: string, id: string) =>
    [...folderKeys.details(), { clientId, campaignId, id }] as const,
};

export const batchKeys = {
  all: ["batches"] as const,
  lists: () => [...batchKeys.all, "list"] as const,
  list: (clientId: string, params?: PaginatedRequestOptions) =>
    [...batchKeys.lists(), { clientId, ...params }] as const,
  details: () => [...batchKeys.all, "detail"] as const,
  detail: (clientId: string, batchId: string) =>
    [...batchKeys.details(), { clientId, batchId }] as const,
};

export const campaignKeys = {
  all: ["campaigns"] as const,
  lists: () => [...campaignKeys.all, "list"] as const,
  list: (
    clientId: string,
    params?: PaginatedRequestOptions & { filters?: FiltersToApplyOnCampaignsPage },
  ) => [...campaignKeys.lists(), { clientId, ...params }] as const,
  details: () => [...campaignKeys.all, "detail"] as const,
  detail: (clientId: string, id: string) => [...campaignKeys.details(), { clientId, id }] as const,
  programTemplatesCampaignId: (clientId: string) =>
    [...campaignKeys.all, "program-templates-id", { clientId }] as const,
};

export type CampaignTemplateListQueryParams = {
  q?: string;
  sort?: string[];
  page?: number;
  size?: number;
  filters?: {
    programType?: string[];
  };
};

export const programStrategyKeys = {
  all: ["program-strategy"] as const,
  schemas: () => [...programStrategyKeys.all, "schema"] as const,
  schema: (programStrategyType: string, version?: number) =>
    [...programStrategyKeys.schemas(), { programStrategyType, version }] as const,
};

export const clientStrategyKeys = {
  all: ["client-strategy"] as const,
  details: () => [...clientStrategyKeys.all, "detail"] as const,
  detail: (clientId: string, strategyCategory: string) =>
    [...clientStrategyKeys.details(), { clientId, strategyCategory }] as const,
};

export const clientStrategySchemaKeys = {
  all: ["client-strategy-schema"] as const,
  schemas: () => [...clientStrategySchemaKeys.all, "schema"] as const,
  schema: (strategyCategory: string) =>
    [...clientStrategySchemaKeys.schemas(), { strategyCategory }] as const,
};

export const campaignTemplateKeys = {
  all: ["campaign-templates"] as const,
  lists: () => [...campaignTemplateKeys.all, "list"] as const,
  list: (params?: CampaignTemplateListQueryParams) =>
    [...campaignTemplateKeys.lists(), { ...params }] as const,
  summaries: () => [...campaignTemplateKeys.all, "summary"] as const,
  summary: (params?: CampaignTemplateListQueryParams) =>
    [...campaignTemplateKeys.summaries(), { ...params }] as const,
  details: () => [...campaignTemplateKeys.all, "detail"] as const,
  detail: (id: string) => [...campaignTemplateKeys.details(), { id }] as const,
  schemas: () => [...campaignTemplateKeys.all, "schema"] as const,
  schema: (version: number) => [...campaignTemplateKeys.schemas(), { version }] as const,
};

export const clientKeys = {
  all: ["clients"] as const,
  lists: () => [...clientKeys.all, "list"] as const,
  list: (params?: GetAllClientsParams) => [...clientKeys.lists(), { ...params }] as const,
  details: () => [...clientKeys.all, "detail"] as const,
  detail: (id: string) => [...clientKeys.details(), { id }] as const,
};

export const clientConfigurationKeys = {
  all: ["client-configurations"] as const,
  client: (clientId: string, configKey: string) =>
    [...clientConfigurationKeys.all, "client", clientId, configKey] as const,
  system: (configKey: string) => [...clientConfigurationKeys.all, "system", configKey] as const,
};

export const themeKeys = {
  all: ["theme"] as const,
  detail: (clientId: string) => [...themeKeys.all, { clientId }] as const,
  googleFonts: () => [...themeKeys.all, "google-fonts"] as const,
  typekitKit: (clientId: string, projectId: string) =>
    [...themeKeys.all, "typekit", { clientId, projectId }] as const,
};

export const userKeys = {
  all: ["users"] as const,
  me: () => [...userKeys.all, "me"] as const,
};

export const managementKeys = {
  all: ["management"] as const,
  info: () => [...managementKeys.all, "info"] as const,
};

export const featureFlagKeys = {
  all: ["feature-flags"] as const,
  list: () => [...featureFlagKeys.all, "list"] as const,
};

export const userLayoutKeys = {
  all: ["user-layout"] as const,
  current: () => [...userLayoutKeys.all, "current"] as const,
};

export const userFlagsKeys = {
  all: ["user-flags"] as const,
  current: () => [...userFlagsKeys.all, "current"] as const,
};

export function normalizeProgramTemplatesParams(
  params?: GetAllProgramTemplatesParams,
): GetAllProgramTemplatesParams {
  if (!params) {
    return { unpaged: true, sort: ["lastModifiedDate,desc"] };
  }
  const { sort, page, size, ...rest } = params;
  return {
    ...rest,
    unpaged: true,
    sort: sort && sort.length > 0 ? sort : ["lastModifiedDate,desc"],
  };
}

export const inboxReviewKeys = {
  all: ["inbox-reviews"] as const,
  lists: () => [...inboxReviewKeys.all, "list"] as const,
  list: (params?: PaginatedRequestOptions & { filters?: InboxFilter }) =>
    [...inboxReviewKeys.lists(), { ...params }] as const,
};

export const programTemplateKeys = {
  all: ["program-templates"] as const,
  lists: () => [...programTemplateKeys.all, "list"] as const,
  list: (clientId: string, params?: GetAllProgramTemplatesParams) =>
    [
      ...programTemplateKeys.lists(),
      { clientId, ...normalizeProgramTemplatesParams(params) },
    ] as const,
  approved: (clientId: string, type: string) =>
    [...programTemplateKeys.all, "approved", { clientId, type }] as const,
};

export const smsSimulatorRuleKeys = {
  all: ["sms-simulator-rules"] as const,
  lists: () => [...smsSimulatorRuleKeys.all, "list"] as const,
  list: () => [...smsSimulatorRuleKeys.lists()] as const,
};

export const smsSimulatorPersonaKeys = {
  all: ["sms-simulator-personas"] as const,
  lists: () => [...smsSimulatorPersonaKeys.all, "list"] as const,
  list: () => [...smsSimulatorPersonaKeys.lists()] as const,
  details: () => [...smsSimulatorPersonaKeys.all, "detail"] as const,
  detail: (id: string) => [...smsSimulatorPersonaKeys.details(), { id }] as const,
};

export const simulationKeys = {
  all: ["simulations"] as const,
  lists: () => [...simulationKeys.all, "list"] as const,
  list: (clientId?: string) =>
    [...simulationKeys.lists(), { clientId: clientId ?? null }] as const,
  details: () => [...simulationKeys.all, "detail"] as const,
  detail: (id: string) => [...simulationKeys.details(), { id }] as const,
};
